import * as stylex from '@stylexjs/stylex';
import { Modal, Button } from '@shared/ui/kit';
import { styles } from './styles';
import { createPortal } from 'react-dom';
import { typography } from '@shared/tokens/typography.stylex';
import { FC, useState } from 'react';
import { SuccessIcon } from '@shared/assets/icons';
import { useFetchDeleteCategories } from '@pages/event/api/useFetchDeleteCategory';
import { ErrorBlock } from '@shared/ui/error-modal';

interface DeleteCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number;
  refetch: () => void;
  countTicket: number;
  title: string;
}

export const DeleteCategoryModal: FC<DeleteCategoryModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  refetch,
  countTicket,
  title,
}) => {
  const [status, setStatus] = useState<'confirm' | 'success' | 'error' | 'secondError'>('confirm');

  const [, deleteCategories] = useFetchDeleteCategories();

  const handleConfirm = async () => {
    const result = await deleteCategories({ categoryIds: [categoryId] });
    if (result.error) {
      setStatus('secondError');
      return;
    }
    const response = result.data?.deleteCategories;
    if (response?.success) {
      setStatus('success');
    } else {
      setStatus('error');
    }
  };

  const handleClose = () => {
    if (status === 'success') {
      refetch();
    }
    setStatus('confirm');
    onClose();
  };

  if (!isOpen) return null;

  const renderModal = (content: JSX.Element) =>
    createPortal(
      <Modal
        active={isOpen}
        settingsHeader={{ height: '16px', padding: '0px' }}
        settingsContainer={{
          height: 'auto',
          width: '480px',
          padding: '0 24px',
        }}
        settingsFooter={{ height: '16px', padding: '0px' }}
      >
        {content}
      </Modal>,
      document.getElementById('modal') as Element
    );

  if (status === 'confirm') {
    return renderModal(
      <div {...stylex.props(styles.container)}>
        <h3 {...stylex.props(styles.header)}>Подтвердить действие?</h3>
        <p {...stylex.props(styles.description)}>Удалить категорию: "{title}"?</p>
        <div {...stylex.props(styles.action)}>
          <Button
            variant="secondary"
            onClick={handleClose}
            size="medium"
            settings={{ height: '36px', width: '85px', fontSize: typography['fontSizeCaption-1'] }}
          >
            Отмена
          </Button>
          <Button
            variant="accent"
            onClick={handleConfirm}
            size="medium"
            settings={{ height: '36px', width: '118px', fontSize: typography['fontSizeCaption-1'] }}
          >
            Подтвердить
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return renderModal(
      <div {...stylex.props(styles.container)}>
        <p {...stylex.props(styles.header)}>Удаление категории</p>
        <div {...stylex.props(styles.successInfo)}>
          <div {...stylex.props(styles.icon)}>
            <SuccessIcon />
          </div>
          <p {...stylex.props(styles.description)}>Вы успешно удалили категорию</p>
        </div>
        <div {...stylex.props(styles.action)}>
          <Button
            onClick={handleClose}
            variant="accent"
            size="medium"
            settings={{ height: '36px', width: '90px', fontSize: typography['fontSizeCaption-1'] }}
          >
            Закрыть
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return renderModal(
      <ErrorBlock
        description={`Вы не можете удалить категорию, так как она содержит ${countTicket} участников`}
        onClose={handleClose}
      />
    );
  }

  return renderModal(
    <ErrorBlock
      description="Произошла непредвиденная ошибка. Попробуйте удалить категорию позднее"
      onClose={handleClose}
    />
  );
};
